import { useEffect, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import DropDown from 'src/components/common/field/DropDown';
import Record from 'src/components/report/payment/Record';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import BreadcrumbsCustom from 'src/components/common/BreadcrumbsCustom';

import { FieldTableType } from 'src/components/common/table/TypeCustom';
import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import Pagin, { PaginationType } from 'src/components/common/Pagin';
import { useNotify } from 'src/context/ContextNotify';
import { getField } from 'src/common/helper';
import dayjs from 'dayjs';

const sxlabelDropdown = {
  '&.Mui-focused': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#AAB2BD', transform: 'translate(14px, 8px) scale(1)' },
};

const sizeDropdown = {
  width: '160px',
  height: '32px'
};

const currentMonth = dayjs().format('YYYY-MM');

const defaultFilter = {
  doctorid: null,
  date: null,
  month: currentMonth,
  category: null,
  paymentmethod: null,
  paymentmethod2: null
};

const defaultDataDropDown = {
  doctorData: [],
  categoriesData: [],
  monthData: [],
  methodData: [],
  method2Data: []
};

const fieldsDefine: FieldTableType[] = [
  {
    label: '日付',
    field: 'day',
    align: 'left',
    width: '100px'
  },
  {
    label: '患者ID',
    field: 'patientno',
    align: 'left',
    width: '100px'
  },
  {
    label: '患者名',
    field: 'fullname',
    align: 'left',
    width: '100px'
  },
  {
    label: '担当医',
    field: 'doctor',
    align: 'left',
    width: '100px'
  },
  {
    label: 'カテゴリー',
    field: 'category',
    align: 'left',
    width: '100px'
  },
  {
    label: '支払方法',
    field: 'method',
    align: 'left',
    width: '100px'
  },
  {
    label: 'カード種別',
    field: 'method2',
    align: 'left',
    width: '100px'
  },
  {
    label: '勘定科目',
    field: 'type',
    align: 'left',
    width: '100px'
  },
  {
    label: '金額',
    field: 'amount',
    align: 'left',
    width: '100px'
  },
  {
    label: '未集金',
    field: 'unpaid',
    align: 'left',
    width: '80px'
  },
  {
    label: '当日売上合計',
    field: 'sales',
    align: 'left',
    width: '100px'
  },
  {
    label: '当日売上の内手術売上',
    field: 'opsales',
    align: 'left',
    width: '100px'
  },
  {
    label: '単日現金合計',
    field: 'cash',
    align: 'left',
    width: '100px'
  },
  {
    label: '単日エアペイ合計',
    field: 'card1',
    align: 'left',
    width: '128px'
  },
  {
    label: '単日アルファ合計',
    field: 'card2',
    align: 'left',
    width: '128px'
  },
  {
    label: '単日ロン合計',
    field: 'loan',
    align: 'left',
    width: '128px'
  },
  {
    label: '前受金合計',
    field: 'advance',
    align: 'left',
    width: '128px'
  },
  {
    label: '単日合計',
    field: 'total',
    align: 'left',
    width: '128px'
  },
  {
    label: 'メモ',
    field: 'note',
    align: 'left',
    width: '128px'
  },
];

const breadcrumbsDefine = [
  {
    to: '/',
    label: '会計'
  },
  {
    label: '売上一覧'
  }
];

type BtnMonthType = 'this_month' | 'last_month' | null;
type FilterType = {
  doctorid: number | null;
  date: string | null;
  month: string | null;
  category: number | null;
  paymentmethod: number | null;
  paymentmethod2: number | null;
};

type DataDropDownType = {
  doctorData: any[];
  categoriesData: any[];
  monthData: any[];
  methodData: any[];
  method2Data: any[];
}

const Payment = () => {
  const notifyContext = useNotify();

  const [filter, setFilter] = useState<FilterType>(defaultFilter);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>();
  const [pagination, setPagination] = useState<PaginationType | undefined>();

  const [dataDropDown, setDataDropDown] = useState<DataDropDownType>(defaultDataDropDown);

  const [loadingDataTable, setLoadingDataTable] = useState(false);

  const [buttonSearchClick, setButtonSearchClick] = useState<BtnMonthType>(null);

  const handlePageChange = (eventL: any, value: number) => {

  };

  const handleClickRow = useCallback(
    (data: any | undefined) => {
      const id = getField(data, 'id', 0);

      if (id) {
      }
    }, []);

  const handleClickButtonMonth = (type: BtnMonthType) => {
    switch (type) {
      case 'this_month':
        handleChangeFilter('month')(currentMonth);
        break;
      case 'last_month':
        if (dataDropDown.monthData.length !== 0) {
          const lastMonth = getField(dataDropDown.monthData[1], 'id', null);
          handleChangeFilter('month')(lastMonth);
        }
        break;
      default:
        break;
    }
    setButtonSearchClick(type);
  };

  const handleSearch = () => {
    getList();
  };

  const handleChangeFilter = (name: string) => (id: number | string) => {
    let reset = {};
    if (name === 'paymentmethod' && Number(id) !== 2) {
      reset = {
        paymentmethod2: null
      };
    }
    setFilter(prevState => ({
      ...prevState,
      ...reset,
      [name]: id !== '' ? id : null
    }))
  };

  const downloadExcel = (data: any, filename: string) => {
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const csvURL = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportExcel = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const URLSearch = repairURLSearch();

      getAPI(
        `/report/export-payment?${URLSearch}token=${token}`
      )
        .then((res: any) => {
          downloadExcel(res, '売上');
        })
        .finally(() => { });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const repairURLSearch = () => {
    const url = Object.entries(filter)
      .filter(([_, value]) => value !== null)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return url.length !== 0 ? url + '&' : url;
  };

  const getList = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setLoadingDataTable(true);

      const URLSearch = repairURLSearch();

      getAPI(`/report/payment?${URLSearch}token=${token}`)
        .then((res: any) => {
          if (res.code === 200) {
            setDataTable(getField(res.data, 'items', []));
            setSummary(getField(res.data, 'summary', {}));

            setDataDropDown({
              doctorData: getField(res.data, 'doctors', []),
              categoriesData: getField(res.data, 'categories', []),
              monthData: getField(res.data, 'months', []),
              methodData: getField(res.data, 'methods', []),
              method2Data: getField(res.data, 'methods2', [])
            });

            setPagination(getField(res.data, 'pagination', {}));
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: getField(res, 'msg', ''),
              type: 'error'
            });
          }
        })
        .finally(() => {
          setLoadingDataTable(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (buttonSearchClick !== null) {
      getList();
      setButtonSearchClick(null);
    }

    // eslint-disable-next-line
  }, [buttonSearchClick]);

  useEffect(() => {
    getList();
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <BreadcrumbsCustom breadcrumbs={breadcrumbsDefine} />
      <Typography
        component='label'
        sx={{
          fontWeight: '500',
          fontSize: '20px',
          lineHeight: '24px'
        }}
      >
        {'売上'}
      </Typography>
      <Stack
        direction={'row'}
        spacing='8px'
        sx={{
          mt: '18px'
        }}
      >
        <Stack
          direction={'row'}
          spacing='8px'
        >
          {!loadingDataTable ? (
            <DropDown
              selected={Number(filter.doctorid)}
              idSelect='doctor-id-select'
              defaultSelect={'選択する'}
              placeholder='医師'
              items={dataDropDown.doctorData}
              onChange={handleChangeFilter('doctorid')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <DropDown
              selected={Number(filter.category)}
              idSelect='category-id-select'
              defaultSelect={'選択する'}
              placeholder='カテゴリー'
              items={dataDropDown.categoriesData}
              onChange={handleChangeFilter('category')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <DropDown
              selected={Number(filter.paymentmethod)}
              idSelect='paymentmethod-id-select'
              defaultSelect={'選択する'}
              placeholder='支払方法'
              items={dataDropDown.methodData}
              onChange={handleChangeFilter('paymentmethod')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {Number(filter.paymentmethod) === 2 && (
            !loadingDataTable ? (
              <DropDown
                selected={Number(filter.paymentmethod2)}
                idSelect='paymentmethod2-id-select'
                defaultSelect={'選択する'}
                placeholder='支払方法'
                items={dataDropDown.method2Data}
                onChange={handleChangeFilter('paymentmethod2')}
                sxInput={sizeDropdown}
                sxLabel={sxlabelDropdown}
                sxMenuProps={{
                  width: sizeDropdown.width
                }}
              />
            ) : (
              <Box width={'160px'}>
                <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
              </Box>
            )
          )}

          {!loadingDataTable ? (
            <DropDown
              selected={filter.month}
              idSelect='month-id-select'
              defaultSelect={'選択する'}
              placeholder='2024年7月'
              items={dataDropDown.monthData}
              onChange={handleChangeFilter('month')}
              sxInput={sizeDropdown}
              sxLabel={sxlabelDropdown}
              svgIconUrl='/images/ic-date-picker.svg'
              sxMenuProps={{
                width: sizeDropdown.width
              }}
            />
          ) : (
            <Box width={'160px'}>
              <Skeleton variant='rectangular' width={'160px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnGradient
              text='検索'
              handleButtonClick={handleSearch}
              sx={{
                minWidth: '60px',
                width: '60px',
              }}
            />
          ) : (
            <Box width={'60px'}>
              <Skeleton variant='rectangular' width={'60px'} height={'32px'} />
            </Box>
          )}
        </Stack>

        <Stack
          direction={'row'}
          justifyContent={'right'}
          width={'100%'}
          spacing={'8px'}
        >
          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='Excel出力'
              handleButtonClick={exportExcel}
              sx={{
                width: '80px'
              }}
              disabled={dataTable.length === 0}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'80px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='今月'
              handleButtonClick={() => handleClickButtonMonth('this_month')}
              sx={{
                width: '80px'
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'80px'} height={'32px'} />
            </Box>
          )}

          {!loadingDataTable ? (
            <BtnOutlineGradient
              text='先月'
              handleButtonClick={() => handleClickButtonMonth('last_month')}
              sx={{
                width: '80px'
              }}
            />
          ) : (
            <Box>
              <Skeleton variant='rectangular' width={'80px'} height={'32px'} />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box sx={{ mt: '27px' }}>
        {!loadingDataTable ? (
          <Record
            items={dataTable}
            summary={summary}
            fields={fieldsDefine}
            onClickRow={handleClickRow}
          />
        ) : (
          <Skeleton variant='rounded' width={'100%'} height={'400px'} />
        )}
      </Box>

      <Box sx={{ width: '100%' }} display={'flex'} justifyContent={'center'}>
        {dataTable.length > 0 && pagination && pagination.page && (
          <Pagin handlePageChange={handlePageChange} pagination={pagination} />
        )}
      </Box>
    </Box>
  );
}

export default Payment;